import { useEffect, useRef, useState } from "react";
import { ImagePlus, X, Loader2, Library, Check, Trash2 } from "lucide-react";
import { api, API_BASE, ApiError } from "../lib/api";
import { useToast } from "../lib/toast";
import { apiErrorMessage } from "../lib/errors";

interface LibraryRow {
  id: string;
  mime: string;
  size: number;
  created_at: number;
}

export interface MediaPickerProps {
  value: string[];
  onChange: (ids: string[]) => void;
  max?: number;
  disabled?: boolean;
  compact?: boolean;
}

const ALLOWED_MIME = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
]);
const MAX_BYTES = 5 * 1024 * 1024;

export function mediaUrl(id: string): string {
  return `${API_BASE}/api/media/${id}`;
}

export function MediaPicker({
  value,
  onChange,
  max = 4,
  disabled,
  compact,
}: MediaPickerProps) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);

  const remaining = Math.max(0, max - value.length);

  async function uploadFiles(files: FileList | File[]) {
    const list = Array.from(files).slice(0, remaining);
    if (list.length === 0) {
      toast.error(`You've reached the max of ${max} images.`);
      return;
    }
    setUploading(true);
    try {
      const newIds: string[] = [];
      for (const f of list) {
        if (!ALLOWED_MIME.has(f.type.toLowerCase())) {
          toast.error(`${f.name}: only PNG / JPEG / WebP / GIF allowed.`);
          continue;
        }
        if (f.size > MAX_BYTES) {
          toast.error(`${f.name}: file is over 5 MB.`);
          continue;
        }
        const form = new FormData();
        form.append("file", f);
        const res = await fetch(`${API_BASE}/api/media`, {
          method: "POST",
          credentials: "include",
          body: form,
        });
        if (!res.ok) {
          const txt = await res.text();
          let msg = `Upload failed (${res.status})`;
          try {
            const j = JSON.parse(txt);
            msg = j.message ?? j.error ?? msg;
          } catch {
            /* ignore */
          }
          throw new ApiError(res.status, "UPLOAD_FAILED", msg);
        }
        const data = (await res.json()) as { id: string };
        newIds.push(data.id);
      }
      if (newIds.length > 0) {
        onChange([...value, ...newIds]);
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function removeAt(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <div
        className={`flex flex-wrap gap-2 ${compact ? "" : "min-h-[88px] rounded-xl border border-dashed border-ink-200/80 bg-ink-50/40 p-2.5"} ${
          dragOver ? "border-brand-400 bg-brand-50/40" : ""
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !uploading && remaining > 0) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (disabled || uploading || remaining === 0) return;
          if (e.dataTransfer.files.length > 0) {
            void uploadFiles(e.dataTransfer.files);
          }
        }}
      >
        {value.map((id, i) => (
          <Thumbnail
            key={id}
            id={id}
            onRemove={() => removeAt(i)}
            disabled={disabled || uploading}
          />
        ))}
        {remaining > 0 ? (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading}
              className={`group flex h-20 w-20 flex-col items-center justify-center gap-0.5 rounded-lg border border-ink-200 bg-white text-xs text-ink-500 transition-colors hover:border-brand-300 hover:bg-brand-50/40 hover:text-brand-700 disabled:opacity-50`}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImagePlus className="h-4 w-4" />
              )}
              <span className="text-2xs font-medium">
                {uploading ? "Uploading…" : "Add image"}
              </span>
              <span className="text-2xs text-ink-400">
                {value.length}/{max}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setShowLibrary(true)}
              disabled={disabled || uploading}
              className="group flex h-20 w-20 flex-col items-center justify-center gap-0.5 rounded-lg border border-ink-200 bg-white text-xs text-ink-500 transition-colors hover:border-brand-300 hover:bg-brand-50/40 hover:text-brand-700 disabled:opacity-50"
            >
              <Library className="h-4 w-4" />
              <span className="text-2xs font-medium">From library</span>
              <span className="text-2xs text-ink-400">previous uploads</span>
            </button>
          </>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              void uploadFiles(e.target.files);
            }
          }}
        />
      </div>
      {!compact ? (
        <p className="mt-1.5 text-2xs text-ink-400">
          PNG / JPEG / WebP / GIF · max 5 MB each · up to {max} per post · drag &
          drop also works
        </p>
      ) : null}
      {showLibrary ? (
        <LibraryModal
          alreadyAdded={value}
          remaining={remaining}
          onPick={(ids) => {
            onChange([...value, ...ids]);
            setShowLibrary(false);
          }}
          onClose={() => setShowLibrary(false)}
        />
      ) : null}
    </div>
  );
}

function LibraryModal({
  alreadyAdded,
  remaining,
  onPick,
  onClose,
}: {
  alreadyAdded: string[];
  remaining: number;
  onPick: (ids: string[]) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const [items, setItems] = useState<LibraryRow[] | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [busyDeleteId, setBusyDeleteId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .get<{ media: LibraryRow[] }>("/api/media?limit=120")
      .then((res) => {
        if (alive) setItems(res.media ?? []);
      })
      .catch((err) => {
        if (alive) {
          toast.error(apiErrorMessage(err));
          setItems([]);
        }
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggle(id: string) {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= remaining) {
        toast.error(`You can only add ${remaining} more.`);
        return prev;
      }
      return [...prev, id];
    });
  }

  async function deleteOne(id: string) {
    setBusyDeleteId(id);
    try {
      await api.del(`/api/media/${id}`);
      setItems((prev) => (prev ?? []).filter((m) => m.id !== id));
      setSelected((prev) => prev.filter((x) => x !== id));
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusyDeleteId(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-ink-900/40 p-4 backdrop-blur-sm animate-fade-in sm:items-center"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-ink-200/60 bg-white shadow-xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-200 px-5 py-3.5">
          <div>
            <h3 className="text-base font-semibold text-ink-900">
              Pick from library
            </h3>
            <p className="text-xs text-ink-500">
              Re-use any image you've previously uploaded.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-500 hover:text-ink-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {items === null ? (
            <div className="flex h-32 items-center justify-center text-sm text-ink-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-ink-200 p-10 text-center text-sm text-ink-500">
              Library is empty. Use <strong>Add image</strong> to upload your
              first one.
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
              {items.map((m) => {
                const inUse = alreadyAdded.includes(m.id);
                const isSelected = selected.includes(m.id);
                const disabled = inUse || busyDeleteId === m.id;
                return (
                  <div
                    key={m.id}
                    className={`group relative aspect-square overflow-hidden rounded-lg border bg-ink-100 transition-all ${
                      isSelected
                        ? "border-brand-400 ring-2 ring-brand-300"
                        : "border-ink-200 hover:border-ink-300"
                    } ${disabled ? "opacity-50" : "cursor-pointer"}`}
                    onClick={() => {
                      if (!disabled) toggle(m.id);
                    }}
                  >
                    <img
                      src={mediaUrl(m.id)}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                    {isSelected ? (
                      <div className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-gradient text-white shadow-sm">
                        <Check className="h-3 w-3" />
                      </div>
                    ) : null}
                    {inUse ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-ink-900/60 text-2xs font-semibold uppercase tracking-wider text-white">
                        Already added
                      </div>
                    ) : null}
                    {!inUse ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void deleteOne(m.id);
                        }}
                        disabled={busyDeleteId === m.id}
                        title="Delete from library"
                        className="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-ink-900/70 text-white opacity-0 transition-opacity hover:bg-red-600 group-hover:opacity-100"
                      >
                        {busyDeleteId === m.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-5 py-3.5">
          <p className="text-xs text-ink-500">
            {items?.length ?? 0} image{items?.length === 1 ? "" : "s"} ·{" "}
            {selected.length} selected · {remaining} slot
            {remaining === 1 ? "" : "s"} free
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onPick(selected)}
              disabled={selected.length === 0}
              className="btn-primary"
            >
              Add {selected.length > 0 ? `(${selected.length})` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Thumbnail({
  id,
  onRemove,
  disabled,
}: {
  id: string;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const [errored, setErrored] = useState(false);
  return (
    <div className="group relative h-20 w-20 overflow-hidden rounded-lg border border-ink-200 bg-ink-100">
      {errored ? (
        <div className="flex h-full w-full items-center justify-center text-2xs text-ink-400">
          ?
        </div>
      ) : (
        <img
          src={mediaUrl(id)}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
          onError={() => setErrored(true)}
        />
      )}
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-ink-900/80 text-white opacity-0 transition-opacity hover:bg-ink-900 group-hover:opacity-100"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
